import React, { useRef } from 'react';
import { ISixDigitInputProps } from '../../types/input.type';
import { ErrorSpan } from './error-span.component';

export const SixDigitInput = ({
  errors,
  name,
  register,
  children,
  setFormattedValue,
  formattedValue
}: ISixDigitInputProps) => {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const formattedRaw = formattedValue.replace(/\s/g, '').padEnd(6, ' ');

  const handleChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 1);
    if (!value) return;

    setFormattedValueFromRaw(formattedRaw, index, value);

    if (index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const setFormattedValueFromRaw = (
    raw: string,
    index: number,
    value: string = ' '
  ) => {
    const chars = raw.split('');
    chars[index] = value;
    const updatedRaw = chars.join('');
    const formatted = updatedRaw.replace(/(.{2})(.{2})(.{2})/, '$1 $2 $3');
    setFormattedValue(formatted);
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Backspace') {
      setFormattedValueFromRaw(formattedRaw, index);

      if (index > 0 && !formattedRaw[index]) {
        inputsRef.current[index - 1]?.focus();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const digits = pastedText.replace(/\D/g, '').slice(0, 6);
    if (!digits) return;

    const newRaw = digits.padEnd(6, ' ');
    const formatted = newRaw.replace(/(.{2})(.{2})(.{2})/, '$1 $2 $3');
    setFormattedValue(formatted);

    const nextIndex = Math.min(digits.length, 5);
    inputsRef.current[nextIndex]?.focus();
  };

  return (
    <div className="flex flex-col relative">
      <div role="group" aria-label="6-digit verification code" className="flex justify-center items-center gap-[12px]">
        {[0, 2, 4].map((groupStart, groupIndex) => (
          <React.Fragment key={groupStart}>
            <div className="flex gap-[8px]">
              {[0, 1].map((offset) => {
                const i = groupStart + offset;
                return (
                  <input
                    key={i}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    aria-label={`Digit ${i + 1} of 6`}
                    value={formattedRaw[i] === ' ' ? '' : formattedRaw[i]}
                    onChange={(e) => handleChange(i, e)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    onPaste={handlePaste}
                    ref={(el) => void (inputsRef.current[i] = el)}
                    className={`
                      w-[44px] h-[44px] m-0
                      text-center text-[20px] font-body
                      rounded-[16px] border 
                      ${errors[name] ? 'border-red-500' : 'border-gray-700'}
                      bg-surface-base text-content-primary caret-blue
                      focus:outline-none focus:border-blue
                      focus:ring-4 focus:ring-[#5DE1FF33]
                      transition-all
                    `}
                  />
                );
              })}
            </div>

            {groupIndex < 2 && (
              <span className="hidden md:inline text-[16px] font-regular text-gray-900">
                -
              </span>
            )}
          </React.Fragment>
        ))}
      </div>

      <input type="hidden" value={formattedValue} {...register(name)} />

      {children}
      {errors[name] && <ErrorSpan>{errors[name].message}</ErrorSpan>}
    </div>
  );
};
